import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import ChatBotify, {
    ChatBotProvider,
    type Flow,
    type Params,
    type Settings,
    type Styles,
    useChatWindow,
} from 'react-chatbotify';
import { useTranslation } from 'react-i18next';

import HtmlRenderer from '@rcb-plugins/html-renderer';
import { marked } from 'marked';

import {
    ChatConfigError,
    fetchWithRetry,
    getChatWebhookUrl,
} from '../../lib/chat-transport';

import ChatBotAvatar from '@/shared/assets/images/chat-bot-avatar.png';
import ChatBotSendButtonIcon from '@/shared/assets/images/chat-bot-send-button.png';


import './chat-bot.module.css';

const CHATBOT_TOGGLE_EVENT = 'chatbot:toggle';
const CHATBOT_STATE_EVENT = 'chatbot:state';

const ChatBotController = memo(() => {
    const { isChatWindowOpen, toggleChatWindow } = useChatWindow();

    useEffect(() => {
        window.dispatchEvent(
            new CustomEvent(CHATBOT_STATE_EVENT, {
                detail: { isOpen: isChatWindowOpen },
            }),
        );
    }, [isChatWindowOpen]);

    useEffect(() => {
        const handleToggle = () => {
            toggleChatWindow();
        };

        window.addEventListener(CHATBOT_TOGGLE_EVENT, handleToggle);

        return () => {
            window.removeEventListener(CHATBOT_TOGGLE_EVENT, handleToggle);
        };
    }, [toggleChatWindow]);

    return null;
});

ChatBotController.displayName = 'ChatBotController';

interface ChatBotProps {
    place?: 'auth' | 'app';
}

const boxShadow = '0px 0px 0px 2px rgba(24, 144, 255, 0.2)' as const;
const primaryColor = '#1890ff' as const;
const authTextColor = 'rgb(0 0 0 / 85%)' as const;
const appTextColor = 'var(--primary-text-color, #000)' as const;

marked.setOptions({
    breaks: true,
    gfm: true,
});

async function sendChatMessage(body: {
    message: string;
    thread?: string;
}): Promise<{
    message: string;
    thread: string;
    message_type: string;
    clear_history: boolean;
}> {
    const webhookUrl = getChatWebhookUrl();

    if (!webhookUrl) {
        // Il segnaposto viene sostituito a runtime dall'entrypoint del container.
        // Se la variabile manca resta stringa vuota (o il segnaposto stesso) e la
        // chat fallisce prima di uscire dal browser: senza questo log l'unica
        // traccia sarebbe un generico "something went wrong", e nei log di
        // backend e n8n non comparirebbe nulla perche' nessuna richiesta parte.
        // eslint-disable-next-line no-console
        console.error(
            '[chatbot] ALGOTECH_ENV_N8N_CHAT_WEBHOOK_URL is not configured: ' +
                'the container is missing the environment variable.',
        );

        throw new ChatConfigError('N8N webhook URL is not configured');
    }

    const thread = body.thread || crypto.randomUUID();

    const res = await fetchWithRetry(webhookUrl, {
        chatInput: body.message,
        sessionId: thread,
    });

    const data = await res.json().catch(() => ({}));

    return {
        message: data.output || data.message || '',
        thread,
        message_type: data.message_type || 'general',
        clear_history: data.clear_history || false,
    };
}

function convertMarkdownToHtml(text: string): string {
    try {
        return marked.parse(text, { async: false }) as string;
    } catch {
        return text;
    }
}

export const ChatBot = memo((props: ChatBotProps) => {
    const { place = 'app' } = props;

    const { t } = useTranslation('translation');

    const currentThread = useRef<string>();

    const isAuthPlace = useMemo<boolean>(() => place === 'auth', [place]);

    const settings = useMemo<Settings>(
        () => ({
            general: {
                primaryColor,
                secondaryColor: '#f5f5f5',
                fontFamily: 'inherit',
                embedded: false,
                flowStartTrigger: 'ON_CHATBOT_INTERACT',
            },
            header: {
                title: t('chatbotTitle'),
                showAvatar: true,
                avatar: ChatBotAvatar,
            },
            tooltip: {
                mode: 'NEVER',
            },
            notification: {
                disabled: true,
                showCount: false,
            },
            chatHistory: {
                disabled: true,
                storageKey: isAuthPlace
                    ? 'rcb-unauthorized-history'
                    : 'rcb-authorized-history',
            },
            chatInput: {
                enabledPlaceholderText: t('chatbotPlaceholder'),
                characterLimit: 250,
                showCharacterCount: true,
                sendButtonIcon: ChatBotSendButtonIcon,
            },
            chatWindow: {
                showScrollbar: true,
                autoJumpToBottom: true,
                defaultOpen: false,
            },
            footer: {
                text: '',
            },
            botBubble: {
                simulateStream: true,
            },
            audio: {
                disabled: true,
            },
            voice: {
                disabled: true,
            },
            emoji: {
                disabled: true,
            },
            fileAttachment: {
                disabled: true,
            },
        }),
        [isAuthPlace, t],
    );

    const styles = useMemo<Styles>(
        () => ({
            tooltipStyle: {
                display: 'none',
            },
            chatButtonStyle: {
                display: 'none',
            },
            toggleButtonStyle: {
                display: 'none',
            },
            chatWindowStyle: {
                position: 'fixed',
                top: 'auto',
                right: 24,
                bottom: 24,
                left: 'auto',
                width: 370,
                height: 500,
                maxHeight: 'calc(100vh - 100px)',
                boxShadow,
                backgroundColor: isAuthPlace
                    ? '#F5F5F5'
                    : 'var(--chatbot-window-bg, #fff)',
                zIndex: 10000,
                overflow: 'hidden',
                borderRadius: 8,
                display: 'flex',
                flexDirection: 'column',
            },
            headerStyle: {
                background: isAuthPlace
                    ? '#FAFAFA'
                    : 'var(--chatbot-header-bg, #fff)',
                borderBottom: `1px solid ${isAuthPlace ? '#F0F0F0' : 'var(--border-color, #f0f0f0)'}`,
                color: isAuthPlace ? authTextColor : appTextColor,
                padding: '12px 16px',
            },
            bodyStyle: {
                backgroundColor: isAuthPlace
                    ? '#F5F5F5'
                    : 'var(--chatbot-window-bg, #fff)',
                paddingBottom: 10,
            },
            footerStyle: {
                display: 'none',
            },
            chatInputContainerStyle: {
                backgroundColor: isAuthPlace
                    ? '#FAFAFA'
                    : 'var(--chatbot-input-container-bg, #fafafa)',
                borderTop: `1px solid ${isAuthPlace ? '#F0F0F0' : 'var(--border-color, #f0f0f0)'}`,
                padding: '8px 12px',
            },
            chatInputAreaStyle: {
                backgroundColor: isAuthPlace
                    ? '#FAFAFA'
                    : 'var(--chatbot-input-container-bg, #fafafa)',
                color: isAuthPlace ? authTextColor : appTextColor,
                border: 'none',
                outline: 'none',
            },
            chatInputAreaFocusedStyle: {
                boxShadow: 'none',
                outline: 'none',
            },
            sendButtonStyle: {
                backgroundColor: primaryColor,
                borderRadius: '50%',
                width: 32,
                height: 32,
            },
            sendButtonHoveredStyle: {
                backgroundColor: 'var(--chatbot-hover-button-bg, #40a9ff)',
            },
            botBubbleStyle: {
                backgroundColor: 'var(--chatbot-bot-bubble-bg, #f0f0f0)',
                color: isAuthPlace ? authTextColor : appTextColor,
                borderRadius: 8,
                padding: '8px 12px',
                maxWidth: '80%',
            },
            userBubbleStyle: {
                backgroundColor: primaryColor,
                color: '#fff',
                borderRadius: 8,
                padding: '8px 12px',
                maxWidth: '80%',
            },
            botOptionStyle: {
                backgroundColor: 'transparent',
                borderRadius: 8,
                color: isAuthPlace ? authTextColor : appTextColor,
                border: `1px solid ${isAuthPlace ? '#D9D9D9' : 'var(--border-color, #d9d9d9)'}`,
                padding: '6px 12px',
            },
            botOptionHoveredStyle: {
                backgroundColor: 'transparent',
                borderRadius: 8,
                color: primaryColor,
                border: `1px solid ${primaryColor}`,
                boxShadow,
            },
            chatHistoryButtonStyle: {
                backgroundColor: isAuthPlace
                    ? '#F0F6FF'
                    : 'var(--chatbot-history-button-bg, #f0f6ff)',
                color: isAuthPlace
                    ? 'rgb(0 0 0 / 65%)'
                    : 'var(--secondary-text-color, rgba(0,0,0,0.65))',
                border: 'none',
            },
            chatHistoryButtonHoveredStyle: {
                backgroundColor: isAuthPlace
                    ? '#D9D9D9'
                    : 'var(--chatbot-history-button-hover-bg, #e6e6e6)',
                color: primaryColor,
            },
        }),
        [isAuthPlace],
    );

    const sendMessageCallback = useCallback(
        async (params: Params) => {
            try {
                const {
                    message,
                    thread,
                    message_type: messageType,
                } = await sendChatMessage({
                    message: params.userInput,
                    ...(currentThread.current
                        ? { thread: currentThread.current }
                        : {}),
                });

                if (
                    !currentThread.current ||
                    currentThread.current !== thread
                ) {
                    currentThread.current = thread;
                }

                const htmlMessage = convertMarkdownToHtml(message);

                await params.injectMessage(htmlMessage);

                switch (messageType) {
                    case 'email_question':
                        await params.goToPath('send_to_email');

                        break;
                    case 'email_input':
                        await params.goToPath('request_email');

                        break;
                    default:
                        break;
                }
            } catch (error) {
                // Ogni fallimento lascia una traccia in console: prima l'unico
                // sintomo era il messaggio generico a schermo, e nei log di
                // backend e n8n non compariva nulla quando la chiamata non
                // partiva affatto (es. webhook non configurato).
                // eslint-disable-next-line no-console
                console.error('[chatbot] send failed:', error);

                if (error instanceof Error) {
                    const errorMessage = error.message;

                    if (
                        errorMessage.includes('may not be blank') ||
                        errorMessage.includes('non può essere omesso')
                    ) {
                        await params.injectMessage(
                            t('chatbotDoubleClickErrorMessage'),
                        );
                    } else {
                        await params.injectMessage(t('chatbotErrorMessage'));
                    }
                } else {
                    await params.injectMessage(t('chatbotErrorMessage'));
                }
            }
        },
        [t],
    );

    const flow = useMemo<Flow>(
        () => ({
            start: {
                message: t('chatbotWelcomeMessage'),
                path: 'user_prompt',
                renderHtml: ['BOT'],
            },
            user_prompt: {
                message: async (params: Params) => {
                    await sendMessageCallback(params);
                },
                path: 'user_prompt',
                renderHtml: ['BOT'],
            },
            send_to_email: {
                options: {
                    items: [t('chatBotYesButton'), t('chatBotNoButton')],
                },
                path: 'user_prompt',
                chatDisabled: true,
            },
            request_email: {
                options: {
                    items: [t('chatbotCancelButton')],
                },
                path: 'user_prompt',
            },
        }),
        [sendMessageCallback, t],
    );

    const plugins = useMemo(() => [HtmlRenderer()], []);

    return (
        <ChatBotProvider>
            <ChatBotify
                flow={flow}
                plugins={plugins}
                settings={settings}
                styles={styles}
            />
            <ChatBotController />
        </ChatBotProvider>
    );
});

ChatBot.displayName = 'ChatBot';
